import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { nama: "", deskripsi: "", url: "", image: [], order: 0 };

export default function PpdbTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/ppdb").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item ? { nama: item.nama, deskripsi: item.deskripsi, url: item.url || "", image: item.image ? [item.image] : [], order: item.order } : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { nama: form.nama, deskripsi: form.deskripsi, url: form.url, image: form.image[0] || "", order: Number(form.order) || 0 };
    try {
      if (dlg === "new") await api.post("/ppdb", payload);
      else await api.put(`/ppdb/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/ppdb/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="ppdb-tab">
      <Button onClick={() => open(null)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 mb-6" data-testid="ppdb-add-button">
        <Plus className="h-4 w-4 mr-2" /> {t("common.add")}
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-hidden flex gap-4" data-testid={`ppdb-item-${p.id}`}>
            {p.image && <img src={imgUrl(p.image)} alt={p.nama} className="w-36 object-cover" />}
            <div className="p-4 flex-1">
              <h4 className="font-semibold text-emerald-950 text-sm">{p.nama}</h4>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.deskripsi}</p>
              {p.url && <p className="text-xs text-emerald-700 mt-1 truncate">{p.url}</p>}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => open(p)} data-testid={`ppdb-edit-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(p.id)} data-testid={`ppdb-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.ppdb")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="ppdb-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Nama Madrasah</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className="mt-1.5" data-testid="ppdb-form-nama" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Deskripsi</Label>
              <Textarea value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} rows={2} className="mt-1.5" data-testid="ppdb-form-deskripsi" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">URL Situs ({t("common.optional")})</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" className="mt-1.5" data-testid="ppdb-form-url" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Urutan</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" data-testid="ppdb-form-order" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Thumbnail</Label>
              <div className="mt-1.5"><ImagePicker values={form.image} onChange={(image) => setForm({ ...form, image })} max={1} testId="ppdb-image-picker" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="ppdb-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
